const PAGE_EVENT_PREFIX = 'breakfast_challenge';
const JOIN_CHALLENGE_MODAL_ID = 'breakfast-challenge-signup-modal';

const sectionMeta = [
  {
    selector: '.page-builder-landing_page_icon_steps',
    id: 'how-it-works',
    name: 'How It Works'
  },
  {
    selector: '.page-builder-landing_page_support_cards',
    id: 'inside-the-challenge',
    name: 'Inside the Challenge'
  },
  {
    selector: '.page-builder-resources_carousel .resources-carousel--resources',
    id: 'resources',
    name: 'Resources'
  },
  {
    selector: '#recipes',
    id: 'recipes',
    name: 'Recipes'
  },
  {
    selector: '.page-builder-science_accordion',
    id: 'research',
    name: 'Research'
  },
  {
    selector: '#join-the-challenge',
    id: 'join-the-challenge',
    name: 'Join the Challenge'
  },
  {
    selector: '.page-builder-resources_carousel .resources-carousel--tips',
    id: 'tips',
    name: 'Tips'
  }
];

const cleanText = (value) =>
  (value || '').replace(/\s+/g, ' ').trim();

const getTimestamp = () => new Date().toISOString();

const pushDataLayer = (eventName, props = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    page_url: window.location.href,
    timestamp: getTimestamp(),
    ...props
  });
};

const getUrlParts = (url) => {
  if (!url) {
    return {};
  }

  try {
    const parsedUrl = new URL(url, window.location.href);

    return {
      url: parsedUrl.href,
      hash: parsedUrl.hash ? parsedUrl.hash.replace('#', '') : ''
    };
  } catch (error) {
    return {
      url,
      hash: url.charAt(0) === '#' ? url.replace('#', '') : ''
    };
  }
};

const getSectionNameFromId = (sectionId) => {
  const meta = sectionMeta.find((section) => section.id === sectionId);

  if (meta) {
    return meta.name;
  }

  return cleanText(sectionId.replace(/-/g, ' '));
};

const getAudience = (element) => {
  const sectionHeading = element
    .closest('section')
    ?.querySelector('h1, h2, h3');
  const context = cleanText(
    `${element.textContent || ''} ${sectionHeading?.textContent || ''}`
  );

  if (
    /(health care|healthcare|provider|quarterly newsletter|get challenge updates|explore free resources|resource & breakfast challenge updates)/i.test(
      context
    )
  ) {
    return 'health_care_provider';
  }

  if (
    /(join the free challenge|explore recipes|see the research|start my challenge)/i.test(
      context
    )
  ) {
    return 'consumer';
  }

  return 'unknown';
};

const getCardTitle = (element) => {
  const card = element.closest('.resources-carousel__card, .card');
  const title = card
    ? card.querySelector(
        '.resources-carousel__card-title, .card__title, [data-recipe-title]'
      )
    : null;

  return cleanText(
    title ? title.getAttribute('data-recipe-title') || title.textContent : ''
  );
};

const getSourceSection = (element) => {
  if (element.closest('.page-builder-landing_page_nav_header')) {
    return {
      id: 'hero',
      name: 'Hero'
    };
  }

  const section = sectionMeta.find((meta) => {
    const sectionElement = document.querySelector(meta.selector);

    return sectionElement ? sectionElement.contains(element) : false;
  });

  if (section) {
    return {
      id: section.id,
      name: section.name
    };
  }

  const sectionElement = element.closest('section');
  const sectionId = sectionElement?.id || '';

  return {
    id: sectionId,
    name: sectionId ? getSectionNameFromId(sectionId) : ''
  };
};

const isJoinChallengeCta = (element) =>
  /^join (the )?(free )?challenge$/i.test(cleanText(element.textContent));

const getJoinChallengeModal = () =>
  document.querySelector(`[data-modal="${JOIN_CHALLENGE_MODAL_ID}"]`);

const closeJoinChallengeModal = () => {
  const modal = getJoinChallengeModal();

  if (!modal) {
    return;
  }

  document.documentElement.classList.remove('html--modal-visible');
  modal.classList.remove('modal--visible');
};

const openJoinChallengeModal = () => {
  const modal = getJoinChallengeModal();

  if (!modal) {
    return;
  }

  if (window.jQuery) {
    window.jQuery(modal).trigger('modal:show');
    return;
  }

  document.documentElement.classList.add('html--modal-visible');
  modal.classList.add('modal--visible');
  modal.setAttribute('tabindex', '-1');
  modal.focus();
};

const initJoinChallengeModalFallback = () => {
  if (window.jQuery) {
    return;
  }

  const modal = getJoinChallengeModal();

  if (!modal) {
    return;
  }

  modal.addEventListener('click', (event) => {
    if (
      event.target === modal ||
      event.target.closest('[data-modal-close]')
    ) {
      event.preventDefault();
      closeJoinChallengeModal();
    }
  });

  document.addEventListener('keyup', (event) => {
    if (event.key === 'Escape') {
      closeJoinChallengeModal();
    }
  });
};

const trackNavClick = (element, interactionType = 'nav_click') => {
  const { url, hash } = getUrlParts(element.getAttribute('href') || element.value);
  const label = cleanText(element.textContent || element.selectedOptions?.[0]?.text);

  pushDataLayer(`${PAGE_EVENT_PREFIX}_nav_click`, {
    bc_interaction_type: interactionType,
    bc_section_id: hash,
    bc_section_name: getSectionNameFromId(hash),
    bc_label: label,
    bc_audience: getAudience(element),
    bc_url: url
  });
};

const trackJoinChallengeClick = (element) => {
  const { url } = getUrlParts(element.getAttribute('href'));
  const sourceSection = getSourceSection(element);

  pushDataLayer(`${PAGE_EVENT_PREFIX}_join_challenge_click`, {
    bc_interaction_type: 'join_challenge_click',
    bc_section_id: sourceSection.id,
    bc_section_name: sourceSection.name,
    bc_label: cleanText(element.textContent),
    bc_url: url
  });
};

const trackCtaClick = (element) => {
  const { url, hash } = getUrlParts(element.getAttribute('href'));

  pushDataLayer(`${PAGE_EVENT_PREFIX}_cta_click`, {
    bc_interaction_type: 'cta_click',
    bc_section_id: hash,
    bc_section_name: getSectionNameFromId(hash),
    bc_label: cleanText(element.textContent),
    bc_audience: getAudience(element),
    bc_url: url
  });
};

const trackResearchClick = (element) => {
  const { url } = getUrlParts(element.getAttribute('href'));
  const panel = element.closest('.science-accordion__panel');
  const panelHeader = panel?.previousElementSibling?.querySelector(
    '.science-accordion__header-title'
  );

  pushDataLayer(`${PAGE_EVENT_PREFIX}_research_click`, {
    bc_interaction_type: 'supporting_research_click',
    bc_label: cleanText(element.textContent),
    bc_research_panel: cleanText(panelHeader?.textContent),
    bc_audience: getAudience(element),
    bc_url: url
  });
};

const trackResourceOrRecipeClick = (element) => {
  const { url } = getUrlParts(element.getAttribute('href'));
  const card = element.closest('.resources-carousel__card');
  const eyebrow = cleanText(
    card?.querySelector('.resources-carousel__card-eyebrow')?.textContent
  );
  const isRecipe =
    element.closest('#recipes') ||
    element.closest('.breakfast-recipes-header') ||
    /recipe/i.test(eyebrow);
  const eventName = isRecipe
    ? `${PAGE_EVENT_PREFIX}_recipe_click`
    : `${PAGE_EVENT_PREFIX}_resource_click`;

  pushDataLayer(eventName, {
    bc_interaction_type: isRecipe ? 'recipe_click' : 'resource_click',
    bc_card_type: eyebrow,
    bc_label: cleanText(element.textContent),
    bc_audience: getAudience(element),
    bc_item_title:
      element.getAttribute('data-recipe-title') || getCardTitle(element),
    bc_url: url
  });
};

const trackRecipeCardClick = (element) => {
  const { url } = getUrlParts(element.getAttribute('href'));
  const itemTitle =
    element.getAttribute('data-recipe-title') || getCardTitle(element);

  pushDataLayer(`${PAGE_EVENT_PREFIX}_recipe_card_click`, {
    bc_interaction_type: 'recipe_card_click',
    bc_label: itemTitle,
    bc_item_title: itemTitle,
    bc_url: url
  });
};

const trackClicks = () => {
  document.addEventListener('click', (event) => {
    const target = event.target.closest('a, button');

    if (!target) {
      return;
    }

    if (target.matches('.landing-page-nav-header__nav-link')) {
      trackNavClick(target);

      if (isJoinChallengeCta(target)) {
        event.preventDefault();
        trackJoinChallengeClick(target);
        openJoinChallengeModal();
      }

      return;
    }

    if (isJoinChallengeCta(target)) {
      event.preventDefault();
      trackJoinChallengeClick(target);
      openJoinChallengeModal();
      return;
    }

    if (
      target.matches(
        '.landing-page-nav-header__button, .two-column-callout__button, #breakfast-stats .landing-page-image-callout__button'
      )
    ) {
      trackCtaClick(target);
      return;
    }

    if (target.closest('.science-accordion') && target.matches('a')) {
      trackResearchClick(target);
      return;
    }

    if (
      target.matches('.resources-carousel__cta') ||
      target.matches('.resources-carousel__card-button') ||
      target.matches('.breakfast-recipes-header a')
    ) {
      trackResourceOrRecipeClick(target);
      return;
    }

    if (target.matches('.page-builder-cards .card--recipe .card__link')) {
      trackRecipeCardClick(target);
    }
  });
};

const getTrackedSectionElements = () =>
  sectionMeta
    .map((meta) => {
      const element = document.querySelector(meta.selector);

      return element ? { element, ...meta } : null;
    })
    .filter(Boolean);

const trackSectionViews = () => {
  const sections = getTrackedSectionElements();
  const viewedSections = new Set();

  if (!sections.length) {
    return;
  }

  const pushSectionView = (section) => {
    if (viewedSections.has(section.id)) {
      return;
    }

    viewedSections.add(section.id);
    pushDataLayer(`${PAGE_EVENT_PREFIX}_section_view`, {
      bc_interaction_type: 'section_view',
      bc_section_id: section.id,
      bc_section_name: section.name
    });
  };

  if (!('IntersectionObserver' in window)) {
    pushSectionView(sections[0]);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        const section = sections.find(({ element }) => element === entry.target);

        if (section) {
          pushSectionView(section);
          observer.unobserve(entry.target);
        }
      });
    },
    {
      root: null,
      rootMargin: '-20% 0px -45% 0px',
      threshold: 0
    }
  );

  sections.forEach(({ element }) => observer.observe(element));
};

const trackTipsSlideChanges = () => {
  if (!window.jQuery) {
    return;
  }

  window
    .jQuery('[data-carousel="resources-tips"]')
    .on('afterChange', (event, slick, currentSlide) => {
      pushDataLayer(`${PAGE_EVENT_PREFIX}_tips_interaction`, {
        bc_interaction_type: 'tips_interaction',
        bc_carousel_action: 'slide_change',
        bc_slide_index: currentSlide,
        bc_slide_title: cleanText(
          slick.$slides
            .eq(currentSlide)
            .find('.resources-carousel__card-title')
            .text()
        )
      });
    });
};

trackClicks();
trackSectionViews();
trackTipsSlideChanges();
initJoinChallengeModalFallback();
